// Square Point of Sale API（モバイルWeb向けアプリ切り替え方式）連携用ヘルパー
// 参考: https://developer.squareup.com/docs/pos-api/build-mobile-web
//       https://developer.squareup.com/docs/pos-api/web-technical-reference
//
// スマートフォン＋Squareリーダー（ドングル）構成が前提。
// PCからはSquare Point of Saleアプリ自体が存在せず起動できないため対象外（呼び出し側でOS判定して出し分ける）。

export type MobileOS = 'ios' | 'android' | 'other'

export function detectMobileOS(userAgent: string = navigator.userAgent): MobileOS {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios'
  if (/Android/i.test(userAgent)) return 'android'
  return 'other'
}

interface BuildSquareDeepLinkParams {
  os: MobileOS
  /** 請求金額（円）。JPYは補助単位を持たないため、そのまま整数で渡す */
  amount: number
  clientId: string
  callbackUrl: string
  note?: string
  /** callback時に紐付けるための識別子（出荷ID）。callback側で一致確認に使う */
  state?: string
}

export function buildSquareDeepLink(params: BuildSquareDeepLinkParams): string | null {
  const { os, amount, clientId, callbackUrl, note, state } = params

  if (os === 'ios') {
    const data = {
      amount_money: { amount, currency_code: 'JPY' },
      callback_url: callbackUrl,
      client_id: clientId,
      version: '1.3',
      notes: note,
      state,
      options: {
        supported_tender_types: ['CREDIT_CARD', 'CASH', 'PAYPAY'],
      },
    }
    return `square-commerce-v1://payment/create?data=${encodeURIComponent(JSON.stringify(data))}`
  }

  if (os === 'android') {
    const extras = [
      'action=com.squareup.pos.action.CHARGE',
      'package=com.squareup',
      `S.browser_fallback_url=${encodeURIComponent(callbackUrl)}`,
      `S.com.squareup.pos.WEB_CALLBACK_URI=${encodeURIComponent(callbackUrl)}`,
      `S.com.squareup.pos.CLIENT_ID=${encodeURIComponent(clientId)}`,
      'S.com.squareup.pos.API_VERSION=v2.0',
      `i.com.squareup.pos.TOTAL_AMOUNT=${amount}`,
      'S.com.squareup.pos.CURRENCY_CODE=JPY',
      'S.com.squareup.pos.TENDER_TYPES=com.squareup.pos.TENDER_CARD,com.squareup.pos.TENDER_CASH,com.squareup.pos.TENDER_PAYPAY',
    ]
    if (note) extras.push(`S.com.squareup.pos.NOTE=${encodeURIComponent(note)}`)
    if (state) extras.push(`S.com.squareup.pos.REQUEST_METADATA=${encodeURIComponent(state)}`)
    return `intent:#Intent;${extras.join(';')};end`
  }

  return null
}

export interface SquareCallbackResult {
  status: 'success' | 'error' | 'none'
  transactionId?: string
  errorCode?: string
  state?: string
}

/** Squareアプリからのcallback（location.search）を解析する */
export function parseSquareCallback(search: string): SquareCallbackResult {
  const searchParams = new URLSearchParams(search)

  // iOS: ?data={JSON}
  const dataParam = searchParams.get('data')
  if (dataParam) {
    try {
      const parsed = JSON.parse(dataParam)
      if (parsed.status === 'ok') {
        return { status: 'success', transactionId: parsed.transaction_id, state: parsed.state }
      }
      return { status: 'error', errorCode: parsed.error_code, state: parsed.state }
    } catch {
      return { status: 'none' }
    }
  }

  // Android: 個別クエリパラメータ
  const transactionId = searchParams.get('com.squareup.pos.SERVER_TRANSACTION_ID')
    || searchParams.get('com.squareup.pos.CLIENT_TRANSACTION_ID')
    || undefined
  const errorCode = searchParams.get('com.squareup.pos.ERROR_CODE') || undefined
  const state = searchParams.get('com.squareup.pos.REQUEST_METADATA') || undefined

  if (transactionId) return { status: 'success', transactionId, state }
  if (errorCode) return { status: 'error', errorCode, state }

  return { status: 'none' }
}
